import React, { useState } from 'react';

import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { SwipeableDrawer, useTheme } from '@mui/material';

import ColumnOpsDrawer from './ColumnOpsDrawer';
import { moveItemToOtherList, reorderItems } from './helpers';
import KanbanColumnArea from './KanbanColumnArea';
import ProvinceOps from './ProvinceOps';
import { KanbanColumn, KanbanItem, KdKanban } from '../../@types/State';
import { useIntelState } from '../../context/IntelState';

// TODO , Never! https://egghead.io/lessons/react-reorder-columns-with-react-beautiful-dnd

const iOS =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

interface Props {
  state: KdKanban;
  isEnemy: boolean;
}

const Kanban: React.FC<Props> = ({ state, isEnemy }) => {
  const theme = useTheme();
  const { intelDispatch } = useIntelState();
  const [selectedColumn, setSelectedColumn] = useState<KanbanColumn | null>(
    null
  );
  const [provinceDrawerOpen, setProvinceDrawerOpen] = useState(false);

  const toggleProvinceDrawer = () => {
    setProvinceDrawerOpen(!provinceDrawerOpen);
  };

  const toggleColumnDrawer = () => {
    if (selectedColumn) {
      setSelectedColumn(null);
    }
  };

  const openColumnDrawer = (el: KanbanColumn) => {
    console.log('openColumnDrawer', el);
    intelDispatch({
      type: 'SET_SELECTED_KANBAN_PROVINCE_ID',
      payload: undefined,
    });
    setSelectedColumn(el);
  };

  const setState = (newState: KdKanban) => {
    intelDispatch({
      type: isEnemy ? 'SET_ENEMY_PLAN' : 'SET_OWN_PLAN',
      payload: newState,
    });
  };

  const handleColumnChange = (e: any, i: number) => {
    const newState = { ...state };
    const col = newState.columns[i];
    col.title = e.target.value;
    setState(newState);
  };

  const handleItemChange = (e: any, colIndex: number, itemInd: number) => {
    const newState = { ...state };
    const col = newState.columns[colIndex];
    const item = col.items[itemInd];
    item.content = e.target.value;
    setState(newState);
  };

  const onDragEnd = (result: any) => {
    const { source, destination } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }

    const srcColIndex = +source.droppableId;
    const destColIndex = +destination.droppableId;

    if (srcColIndex === destColIndex) {
      const newItems = reorderItems(
        state.columns[srcColIndex].items,
        source.index,
        destination.index
      );
      const newState = { ...state };
      newState.columns[srcColIndex].items = newItems;
      setState(newState);
    } else {
      const result = moveItemToOtherList(
        state.columns[srcColIndex],
        state.columns[destColIndex],
        source,
        destination
      );
      const newState = { ...state };
      newState.columns[srcColIndex].items = result[srcColIndex].items;
      newState.columns[destColIndex].items = result[destColIndex].items;
      setState(newState);
    }
  };

  const addColumnOp = (op: string) => {
    if (!selectedColumn) return;
    if (!selectedColumn.requiredOps) {
      selectedColumn.requiredOps = [];
      selectedColumn.requiredOps.push(op);
      selectedColumn.items.forEach((item: KanbanItem) =>
        intelDispatch({
          type: 'ADD_PROVINCE_OP',
          payload: { op, province: item.province },
        })
      );
    } else if (selectedColumn.requiredOps.indexOf(op) < 0) {
      selectedColumn.requiredOps.push(op);
      selectedColumn.items.forEach((item: KanbanItem) =>
        intelDispatch({
          type: 'ADD_PROVINCE_OP',
          payload: { op, province: item.province },
        })
      );
    }
  };

  const deleteColumnOp = (op: string, column: KanbanColumn) => {
    if (!column || !column.requiredOps) return;
    const index = column.requiredOps.indexOf(op);
    if (index > -1) {
      column.requiredOps.splice(index, 1);
    }
    column.items.forEach((item: KanbanItem) =>
      intelDispatch({
        type: 'DELETE_PROVINCE_OP',
        payload: { op, province: item.province },
      })
    );
  };

  const deleteCol = (colInd: number) => {
    const newState = { ...state };
    newState.columns.splice(colInd, 1);
    setState(newState);
  };

  const handleCollapse = (item: KanbanItem, isExpanded: boolean) => {
    intelDispatch({
      type: 'SET_SELECTED_KANBAN_PROVINCE_ID',
      payload: isExpanded ? item.id : undefined,
    });
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        {state.columns.map((el: KanbanColumn, colInd: number) => (
          <Droppable key={colInd} droppableId={`${colInd}`}>
            {(provided, snapshot) => (
              <KanbanColumnArea
                column={el}
                colInd={colInd}
                theme={theme}
                handleCollapse={handleCollapse}
                handleItemChange={handleItemChange}
                deleteColumnOp={deleteColumnOp}
                toggleProvinceDrawer={toggleProvinceDrawer}
                openColumnDrawer={openColumnDrawer}
                handleColumnChange={handleColumnChange}
                provided={provided}
                snapshot={snapshot}
                isWarPlan={isEnemy}
                deleteCol={deleteCol}
              />
            )}
          </Droppable>
        ))}
      </DragDropContext>
      <SwipeableDrawer
        anchor='right'
        open={provinceDrawerOpen}
        onClose={toggleProvinceDrawer}
        onOpen={toggleProvinceDrawer}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
      >
        <ProvinceOps isWarPlan={isEnemy} />
      </SwipeableDrawer>
      <ColumnOpsDrawer
        drawerOpen={!!selectedColumn}
        toggleColumnDrawer={toggleColumnDrawer}
        addColumnOp={addColumnOp}
        deleteColumnOp={deleteColumnOp}
        selectedColumn={selectedColumn}
      />
    </>
  );
};

export default Kanban;
